import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, XIcon } from 'lucide-react';
import { INSIDE_PAGES, INSIDE_PAGES_POINTS, InsidePage } from '../data/insidePages';

/**
 * Muestra real del producto: 6 páginas internas del manual. En mobile es un
 * carrusel con scroll horizontal (una página casi a ancho completo y la
 * siguiente asomando); en desktop, grilla de 3 columnas. Tocar una página la
 * abre en grande para leerla.
 */
export function InsidePagesSection() {
  const [active, setActive] = useState<InsidePage | null>(null);
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(null);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [active]);
  return <section id="por-dentro" className="bg-cream py-11 sm:py-14" data-id="element-144" data-mp="3udw8lvqxjt3m">
      <div className="mx-auto max-w-md sm:max-w-4xl" data-id="element-145" data-mp="3pejkl7obofzg">
        <div className="px-5 text-center" data-id="element-146" data-mp="3pfurvhgz85vb">
          <h2 className="mx-auto max-w-[22ch] font-display text-[1.4rem] font-extrabold leading-[1.15] tracking-tight text-charcoal sm:text-[2rem]" data-id="element-147" data-mp="78wtnv5l4vfq">
            Mirá por dentro lo que <span className="text-gold-deep" data-id="element-148" data-mp="3966e1f9oxfy">vas a recibir</span>
          </h2>
          <p className="mx-auto mt-2.5 max-w-[46ch] font-sans text-[0.86rem] leading-relaxed text-taupe" data-id="element-149" data-mp="h4qhiaor8bct">
            Estas son páginas reales del manual para que veas la calidad, el nivel de detalle y la
            variedad de fórmulas, técnicas y diseños que vas a poder crear.
          </p>
        </div>

        <ul className="mt-6 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-3 [scrollbar-width:none] sm:grid sm:grid-cols-3 sm:gap-5 sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden" aria-label="Páginas internas del manual" data-id="element-150" data-mp="lze424076vor">
          {INSIDE_PAGES.map(page => <li key={page.src} className="w-[78%] shrink-0 snap-center sm:w-auto" data-id="element-151" data-mp="3tp23kz2naywm">
              <button type="button" onClick={() => setActive(page)} className="group block w-full text-left focus:outline-none focus-visible:rounded-xl focus-visible:ring-2 focus-visible:ring-gold" aria-label={`Ver en grande: ${page.title}`} data-id="element-152" data-mp="3tmxyidt0ftur">
                <img src={page.src} alt={`Página interna del manual: ${page.title}`} loading="lazy" decoding="async" width={1622} height={2294} sizes="(max-width: 640px) 78vw, 300px" className="aspect-[1622/2294] w-full rounded-lg border border-stone bg-white object-cover shadow-[0_22px_40px_-28px_rgba(46,35,64,0.55)] transition-transform duration-200 ease-out group-hover:-translate-y-1" data-id="element-153" data-mp="g9vztydj6ecg" />
                <p className="mt-2.5 font-display text-[0.6rem] font-bold uppercase tracking-[0.1em] text-gold-deep" data-id="element-154" data-mp="2jk935cavm2t5">
                  {page.tomo}
                </p>
                <p className="mt-0.5 font-display text-[0.84rem] font-bold leading-tight text-charcoal" data-id="element-155" data-mp="29s3fgernq7jr">
                  {page.title}
                </p>
              </button>
            </li>)}
        </ul>

        <ul className="mx-5 mt-5 grid gap-2 rounded-2xl border border-gold bg-gold-soft px-4 py-4 sm:mx-auto sm:max-w-3xl sm:grid-cols-3 sm:gap-4 sm:px-5" data-id="element-156" data-mp="32g90z3qjc0kn">
          {INSIDE_PAGES_POINTS.map(point => <li key={point} className="flex gap-2 font-sans text-[0.83rem] font-semibold leading-snug text-charcoal" data-id="element-157" data-mp="2tfari5xbv0uo">
              <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" aria-hidden strokeWidth={3} data-id="element-158" data-mp="1i28dnimm4pe7" />
              {point}
            </li>)}
        </ul>
      </div>

      <AnimatePresence data-id="element-159" data-mp="30zad7on7bj0q">
        {active && <motion.div key="lightbox" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/80 p-4" role="dialog" aria-modal="true" aria-label={active.title} onClick={() => setActive(null)} data-id="element-160" data-mp="30yifa764t97a">
            <motion.img src={active.src} alt={`Página interna del manual: ${active.title}`} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-h-[88vh] w-auto max-w-full rounded-lg bg-white object-contain" onClick={e => e.stopPropagation()} data-id="element-161" data-mp="3opee3o9094o1" />
            <button type="button" onClick={() => setActive(null)} className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-charcoal focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" aria-label="Cerrar" data-id="element-162" data-mp="1vrhgi64t7dl7">
              <XIcon className="h-5 w-5" aria-hidden strokeWidth={2.5} data-id="element-163" data-mp="20rn3h904wbwy" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </section>;
}